import { logger } from "@/utils/logger";
import { useCallback } from 'react';
import { useAnalyticsTracking } from './useAnalyticsTracking';

interface UseChatActionsProps {
  input: string;
  setInput: (value: string) => void;
  isLoading: boolean;
  sendMessage: (message: string, formData?: { [key: string]: string }, inventoryFilter?: any, l1Title?: string | null, showroomFilter?: any, isFromPromptMenu?: boolean) => Promise<void>;
  recordActivity: () => void;
  trackMessage?: (messageContent: string, messageType: 'user' | 'agent', formType?: string) => void;
  getCurrentInventoryFilter?: () => any;
  getCurrentL1Title?: () => string | null;
  getShowroomFilter?: () => any;
}

export const useChatActions = ({
  input,
  setInput,
  isLoading,
  sendMessage,
  recordActivity,
  trackMessage,
  getCurrentInventoryFilter,
  getCurrentL1Title,
  getShowroomFilter
}: UseChatActionsProps) => {
  const { trackChatInteraction, trackUserMessageSent } = useAnalyticsTracking();

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    
    // Record activity to reset inactivity timer
    recordActivity();
    
    // Track chat interaction and user message
    trackChatInteraction('user_message', 'text_input');
    trackUserMessageSent(userMessage.length);
    
    // Track for Shift Digital
    if (trackMessage) {
      trackMessage(userMessage, 'user');
    }
    
    // Get inventory filter and showroom filter if available
    const inventoryFilter = getCurrentInventoryFilter?.();
    const l1Title = getCurrentL1Title?.();
    const showroomFilter = getShowroomFilter?.();
    logger.log('📦 Retrieved inventory filter for message:', inventoryFilter);
    logger.log('🏢 Retrieved L1 title for message:', l1Title);
    logger.log('🏢 Retrieved showroom filter for message:', showroomFilter);
    
    // Clear the filter after retrieving it so it doesn't persist to next message
    if (inventoryFilter) {
      sessionStorage.removeItem('currentInventoryFilter');
    }
    
    await sendMessage(userMessage, undefined, inventoryFilter, l1Title, showroomFilter);
  }, [input, isLoading, setInput, recordActivity, trackChatInteraction, trackUserMessageSent, sendMessage, getCurrentInventoryFilter, getCurrentL1Title]);

  const handleSendMessage = useCallback(async (message?: string) => {
    // Record activity to reset inactivity timer
    recordActivity();
    
    // Update page context before sending message
    try {
      const { PageContextManager } = await import('../utils/pageContextManager');
      PageContextManager.updatePageContext();
    } catch (error) {
      console.error('Error updating page context:', error);
    }
    
    // Get inventory filter and showroom filter if available (for prompt menu interactions)
    const inventoryFilter = getCurrentInventoryFilter?.();
    const l1Title = getCurrentL1Title?.();
    const showroomFilter = getShowroomFilter?.();
    if (inventoryFilter) {
      logger.log('📦 Retrieved inventory filter for button message:', inventoryFilter);
      logger.log('🏢 Retrieved L1 title for button message:', l1Title);
      logger.log('🏢 Retrieved showroom filter for button message:', showroomFilter);
      // Clear the filter after retrieving it so it doesn't persist to next message
      sessionStorage.removeItem('currentInventoryFilter');
    }
    
    // If a message is provided (from buttons), use it directly
    if (message && message.trim()) {
      // Track chat interaction for button clicks
      trackChatInteraction('button_click', 'quick_reply');
      trackUserMessageSent(message.trim().length);
      
      // Track for Shift Digital
      if (trackMessage) {
        trackMessage(message.trim(), 'user');
      }
      
      await sendMessage(message.trim(), undefined, inventoryFilter, l1Title, showroomFilter, true);
      return;
    }
    
    // Otherwise, use the input value (from text input)
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    
    // Track chat interaction for text input
    trackChatInteraction('user_message', 'text_input');
    trackUserMessageSent(userMessage.length);
    
    // Track for Shift Digital
    if (trackMessage) {
      trackMessage(userMessage, 'user');
    }
    
    await sendMessage(userMessage, undefined, inventoryFilter, l1Title, showroomFilter);
  }, [input, isLoading, setInput, recordActivity, trackChatInteraction, trackUserMessageSent, sendMessage, trackMessage, getCurrentInventoryFilter, getCurrentL1Title, getShowroomFilter]);

  return {
    handleSubmit,
    handleSendMessage,
  };
};
